// app/lib/api.ts
//
// Phase 5: every backend call goes through fetchWithAuth, which attaches an
// Entra ID token (silently renewed by MSAL) and handles 401 / 403 responses.

import {
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from "@azure/msal-browser";

import { loginRequest, msalInstance } from "~/features/auth/msalConfig";
import { trackAuthSignInFailed } from "~/lib/analytics";

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL + "/api/v1") as string;

export const RAG_STREAM_URL =
  "https://csnx-infocenter-rag-tonnt75awq-uc.a.run.app/csnx/chat-with-tools/stream";

/**
 * Fetch wrapper that attaches the Entra ID token and unwraps common auth
 * failures. Pass paths starting with "/" — they get prefixed with API_BASE_URL.
 *
 * For absolute URLs (e.g. RAG_STREAM_URL on a different host), pass the full
 * URL; we still attach the auth header since both the backend and RAG
 * service eventually validate the same token (RAG gating is Phase 5b).
 */
export async function fetchWithAuth(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error("Not authenticated");
  }

  let token: string;
  try {
    const result: AuthenticationResult = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    token = result.idToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Refresh token expired — kick to login.
      await msalInstance.logoutRedirect();
      throw new Error("Session expired");
    }
    throw e;
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, { ...init, headers });

  if (response.status === 401) {
    await msalInstance.logoutRedirect();
    throw new Error("Session expired");
  }
  if (response.status === 403) {
    const body = await response.clone().json().catch(() => ({}));
    if (body.error === "domain_not_allowed") {
      trackAuthSignInFailed({ reason: "domain_not_allowed" });
      await msalInstance.logoutRedirect();
      throw new Error("Access revoked");
    }
    // other 403s (not_uploader, not_admin) bubble up as the response
  }
  return response;
}
